import _ from 'lodash'
import { shouldShowCallTodayIndicator } from './ParticipantListItem/callTodayUtils'
import { HomeViewParticipantFragment } from './searchParticipants.generated'

type Participant = HomeViewParticipantFragment

export const PRIORITY_FILTERS = ['safety', 'reEngage', 'followUp', 'waiting', 'onTrack'] as const

export type PriorityFilter = (typeof PRIORITY_FILTERS)[number]

export type PriorityFilterCounts = Record<PriorityFilter, number>

export const PRIORITY_FILTER_LABELS: Record<PriorityFilter, string> = {
  safety: 'Safety',
  reEngage: 'Re-engage',
  followUp: 'Follow-up',
  waiting: 'Waiting',
  onTrack: 'On track',
}

const countShown = (...indicators: Array<unknown>) => indicators.filter(Boolean).length

const countIndicators: Record<PriorityFilter, (participant: Participant, hasDraftMessage: boolean) => number> = {
  safety: (participant) => countShown(participant.indicators?.risk),
  reEngage: (participant) => countShown(participant.indicators?.daysDisengaged),
  followUp: (participant) =>
    countShown(
      participant.indicators?.overdueHealthQuestionnaire,
      participant.indicators?.monthlySummaryNotes?.showIndicator,
      participant.indicators?.completedCallAwaitingNote
    ),
  waiting: (participant, hasDraftMessage) =>
    countShown(
      shouldShowCallTodayIndicator(participant.indicators?.currentOrUpcomingCall),
      participant.indicators?.newMessages?.count,
      hasDraftMessage
    ),
  // On track has no indicator of its own: it is everyone outside the other groups, and each
  // such participant counts once, so its pill shows a participant count.
  onTrack: (participant, hasDraftMessage) =>
    PRIORITY_FILTERS.some((filter) => filter !== 'onTrack' && countIndicators[filter](participant, hasDraftMessage) > 0)
      ? 0
      : 1,
}

const countIn = (filter: PriorityFilter, participants: Array<Participant>, uidsWithDraftMessage: Set<string>) =>
  _.sumBy(participants, (participant) =>
    countIndicators[filter](participant, uidsWithDraftMessage.has(participant.uid))
  )

export const countPriorityIndicators = (
  participants: Array<Participant>,
  uidsWithDraftMessage: Set<string>
): PriorityFilterCounts => ({
  safety: countIn('safety', participants, uidsWithDraftMessage),
  reEngage: countIn('reEngage', participants, uidsWithDraftMessage),
  followUp: countIn('followUp', participants, uidsWithDraftMessage),
  waiting: countIn('waiting', participants, uidsWithDraftMessage),
  onTrack: countIn('onTrack', participants, uidsWithDraftMessage),
})

/**
 * The priority groups a participant currently belongs to — a participant can be in
 * several of the indicator groups, or else only in On track, so this is never empty.
 * Derived from the same per-group predicates the pills filter on, so analytics bands can
 * never drift from what the filters actually show.
 */
export const getParticipantPriorityBands = (
  participant: Participant,
  hasDraftMessage: boolean
): Array<PriorityFilter> =>
  PRIORITY_FILTERS.filter((filter) => countIndicators[filter](participant, hasDraftMessage) > 0)

export const isParticipantOnTrack = (participant: Participant, hasDraftMessage: boolean) =>
  countIndicators.onTrack(participant, hasDraftMessage) > 0

export const participantMatchesPriorityFilters = (
  participant: Participant,
  hasDraftMessage: boolean,
  selectedFilters: Array<PriorityFilter>
) => selectedFilters.some((filter) => countIndicators[filter](participant, hasDraftMessage) > 0)
